import type { Role } from "./workspace";

/** FR-ACC-05: everything a role may do. The API enforces it; the UI hides what a role cannot use. */
export const PERMISSIONS = [
  "report.create",
  "report.view",
  "report.edit",
  "report.editOwn",
  "report.comment",
  "report.jira",
  "report.delete",
  "members.manage",
  "settings.manage",
  "workspace.delete",
  "workspace.transfer",
] as const;

export type Permission = (typeof PERMISSIONS)[number];

const MEMBER: Permission[] = ["report.create", "report.view", "report.comment", "report.editOwn"];
const DEVELOPER: Permission[] = [...MEMBER, "report.edit", "report.jira"];
const ADMIN: Permission[] = [...DEVELOPER, "report.delete", "members.manage", "settings.manage"];
const OWNER: Permission[] = [...ADMIN, "workspace.delete", "workspace.transfer"];

const MATRIX: Record<Role, ReadonlySet<Permission>> = {
  OWNER: new Set(OWNER),
  ADMIN: new Set(ADMIN),
  DEVELOPER: new Set(DEVELOPER),
  TESTER: new Set(MEMBER),
};

export function can(role: Role, permission: Permission): boolean {
  return MATRIX[role].has(permission);
}

const RANK: Record<Role, number> = { OWNER: 4, ADMIN: 3, DEVELOPER: 2, TESTER: 1 };

/** Admins manage members up to their own level; only an Owner can touch Owners (FR-ACC-05). */
export function canManageRole(actor: Role, target: Role): boolean {
  if (!can(actor, "members.manage")) return false;
  return actor === "OWNER" || RANK[target] < RANK.OWNER;
}

/** True when `actor` ranks strictly above `target` (an Admin over a Developer, an Owner over an Admin). */
export function outranks(actor: Role, target: Role): boolean {
  return RANK[actor] > RANK[target];
}
